// Helpers for the Add product flow: turning extracted fields into questions,
// answers into fields, and draft state into something small enough to save.
import { DEFAULT_CHANNELS, PRODUCT_FIELDS, isEmptyValue } from '../product/productUtils';

export const STEPS = [
    { id: 'sources', label: 'Sources' },
    { id: 'review', label: 'Review' },
    { id: 'quiz', label: 'Fill gaps' },
    { id: 'icps', label: 'ICPs' },
    { id: 'first', label: 'First posts' },
];

export const MAX_QUESTIONS = 6;
export const QUIZ_SOURCE = 'Your quiz answers';

const GOAL_OPTIONS = [
    { label: 'More trials / sign-ups from social', evidence: '' },
    { label: 'Brand awareness', evidence: '' },
    { label: 'Sales of a specific offer', evidence: '' },
    { label: 'Grow an audience / followers', evidence: '' },
];

const FALLBACK_QUESTIONS = {
    name: { question: "What's your product called?", helper: 'The name customers know it by.' },
    oneLiner: { question: 'How would you describe it in one line?', helper: 'What it does and for whom, like you would to a friend.' },
    category: { question: 'What category is it in?', helper: 'e.g. Health & Fitness, B2B software, skincare.' },
    platforms: { question: 'Where can people use or buy it?', helper: 'e.g. iOS, Android, Web, Shopify store. Separate with commas.' },
    pricing: { question: 'How is it priced?', helper: 'Free tier, trial, plans, one-off price…' },
    features: { question: 'What are its key features?', helper: 'Separate with commas.' },
    proof: { question: 'Any proof you can point to?', helper: 'A rating, a review quote, a customer count.' },
    competitors: { question: 'Who do customers compare you with?', helper: 'Separate with commas.' },
    targetCustomer: { question: 'Who do you most want to reach first?', helper: 'Pick any that fit, or describe them.', multi: true },
    goal: { question: "What's the main goal for your content?", helper: 'We plan and measure content against it.', options: GOAL_OPTIONS },
    offer: { question: 'Are you running an offer or promo right now?', helper: 'e.g. First week free, 20% off annual plans.', options: [{ label: 'No offer right now', evidence: '' }] },
};

export const emptyFields = () => PRODUCT_FIELDS.reduce((acc, field) => ({
    ...acc,
    [field.id]: { value: null, source: '', confidence: 'low' },
}), {});

export const fallbackQuestion = (field) => ({
    field,
    multi: false,
    options: [],
    ...FALLBACK_QUESTIONS[field],
    question: FALLBACK_QUESTIONS[field]?.question || `What's the ${field}?`,
});

// Fields that still need an answer: not found, or low confidence and not edited by the user
export const gapFields = (fields) => PRODUCT_FIELDS
    .map(field => field.id)
    .filter(id => {
        const field = fields?.[id];
        if (!field) return true;
        if (field.edited) return isEmptyValue(field.value);
        return isEmptyValue(field.value) || field.confidence === 'low';
    });

// One question per gap: the server's question when it wrote one, a generic one otherwise
export const questionsForGaps = (fields, serverQuestions = []) => {
    const gaps = gapFields(fields);
    const ordered = [
        ...serverQuestions.map(q => q.field).filter(field => gaps.includes(field)),
        ...['targetCustomer', 'goal', 'offer', 'competitors'].filter(field => gaps.includes(field)),
        ...gaps,
    ];
    return [...new Set(ordered)]
        .slice(0, MAX_QUESTIONS)
        .map(field => serverQuestions.find(q => q.field === field) || fallbackQuestion(field));
};

export const answerValues = (answer) => [
    ...(answer?.selected || []),
    ...(answer?.other?.trim() ? [answer.other.trim()] : []),
];

export const isAnswered = (answer) => !answer?.skipped && answerValues(answer).length > 0;

// Turns a quiz answer into the field's value shape
export const answerToValue = (field, answer, previous) => {
    const values = answerValues(answer).filter(value => value !== 'No offer right now');
    const split = values.flatMap(value => value.split(',')).map(value => value.trim()).filter(Boolean);
    if (field === 'platforms' || field === 'competitors') return split;
    if (field === 'features') return split.map(name => ({ name, detail: '' }));
    if (field === 'proof') {
        return { rating: previous?.rating ?? null, ratingCount: previous?.ratingCount ?? null, quotes: values };
    }
    if (field === 'offer' && answerValues(answer).includes('No offer right now') && values.length === 0) return '';
    return values.join(', ');
};

export const applyAnswers = (fields, questions, answers) => questions.reduce((acc, question) => {
    const answer = answers[question.field];
    if (!isAnswered(answer)) return acc;
    return {
        ...acc,
        [question.field]: {
            value: answerToValue(question.field, answer, acc[question.field]?.value),
            source: QUIZ_SOURCE,
            confidence: 'high',
            edited: true,
        },
    };
}, fields);

// For the ICP generator: { targetCustomer: ["Nurses and shift workers"], goal: [...] }
export const answersForServer = (answers) => Object.entries(answers).reduce((acc, [field, answer]) => (
    isAnswered(answer) ? { ...acc, [field]: answerValues(answer) } : acc
), {});

// Product fields to save from the extracted / edited fields
export const fieldsToProduct = (fields) => PRODUCT_FIELDS.reduce((acc, field) => {
    const value = fields?.[field.id]?.value;
    if (isEmptyValue(value)) return acc;
    return { ...acc, [field.id]: value };
}, {});

export const fieldSourcesOf = (fields) => Object.entries(fields || {}).reduce((acc, [id, field]) => (
    isEmptyValue(field.value) ? acc : { ...acc, [id]: { source: field.source || '', confidence: field.confidence || 'low' } }
), {});

// Sources as stored on the product: no file data, and page text only for pasted text
export const sourcesToProduct = (sources, hasQuiz) => [
    ...sources.filter(source => source.status === 'ready').map(source => ({
        id: source.id,
        type: source.type,
        title: source.title,
        detail: source.detail,
        ...(source.url && { url: source.url }),
        ...(source.icon && { icon: source.icon }),
        ...(source.type === 'text' && { text: source.text }),
        addedAt: source.addedAt,
    })),
    ...(hasQuiz ? [{ id: 'quiz', type: 'quiz', title: QUIZ_SOURCE, detail: '', addedAt: new Date().toISOString() }] : []),
];

// The draft saved on the product so "Save & exit" can resume later
export const serializeDraft = (draft) => ({
    ...draft,
    sources: draft.sources
        .filter(source => source.status === 'ready')
        .map(({ file, text, error, ...source }) => ({
            ...source,
            ...(source.type === 'text' && { text }),
            ...(file && { file: { name: file.name, size: file.size } }),
        })),
});

export const channelsFromIcps = (icps) => {
    const names = [...new Set(icps.flatMap(icp => icp.channels || []))]
        .map(name => (/youtube/i.test(name) ? 'YouTube Shorts' : name))
        .filter((name, index, list) => list.indexOf(name) === index)
        .slice(0, 4);
    return (names.length ? names : DEFAULT_CHANNELS).map(name => ({ name, connected: false, handle: '' }));
};

export const readFileAs = (file, method) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader[method](file);
});

export const formatBytes = (bytes = 0) => (bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`);
